// The result gallery: the evaluation and download surface for one run.
//
// This is a contact sheet, not a player. Every result is a tile in a grid,
// at a fixed size, with its coordinates printed underneath -- so 24 results
// can be compared at a glance without anything taking over the screen. One
// tile at a time can be inspected in a side panel (fixed 460px, never
// fullscreen) to check it properly or pull it out on its own; the whole
// batch is downloaded from a bar at the bottom.
//
// Two readings of the same coordinates (lib/results.ts): "1:1", every
// result once, flat; and "1:N", grouped under the input photo it came from.
// Nothing in the data distinguishes them -- they are two ways of reading
// `slot` -- so both are built from the same grouping functions.
//
// Byte fetching: the whole batch download needs the artifact bytes, which
// only script can fetch (bearer token, 08-contracts.md #4). Previews are
// fetched eagerly for the first PREVIEW_EAGER results and on demand after
// that, because 100 videos fetched up front is a lot of memory for a page
// someone may only be skimming.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  cancelTask,
  getArtifact,
  getTask,
  type ArtifactView,
  type ResultSlot,
  type TaskView,
} from "../api/client";
import { fetchStoreBytes, fetchStoreObjectURL, StoreError } from "../api/store";
import { formatMinor, formatWhen } from "../lib/format";
import { useCatalog } from "../lib/catalog";
import Drawer, { panelPageClass } from "../components/Drawer";
import { useFlip } from "../lib/flip";
import {
  defaultGroupDim,
  downloadFileName,
  extensionFor,
  formatBytes,
  groupSlots,
  isVideo,
  resultFileName,
  shareLink,
  slotDims,
  slotLabel,
  totalBytes,
} from "../lib/results";
import { buildZip, sanitizeName, uniqueNames, ZIP_MAX_TOTAL_BYTES, type ZipEntry } from "../lib/zip";

const ACTIVE_STATES = new Set(["QUEUED", "RUNNING"]);
const TERMINAL_TASK_STATES = new Set(["DONE", "FAILED", "CANCELLED"]);
/** How many results get a preview fetched without being asked. */
const PREVIEW_EAGER = 24;

/** The `image` input array, in slot order -- what each "1:N" group came
 * from. TaskView types `inputs` as an opaque record (08-contracts.md #1
 * does not enumerate per-alias inputs in the schema), so this is the one
 * place that reads into it. */
function inputImageIds(task: TaskView): string[] {
  const inputs = task.inputs as unknown as { image?: unknown };
  if (!Array.isArray(inputs?.image)) return [];
  return inputs.image.filter((v): v is string => typeof v === "string");
}

/** Hand a blob to the browser as a download. A Store artifact is behind a
 * bearer token, so there is no URL to link to -- the bytes have to be
 * fetched, wrapped, and clicked programmatically. */
function saveBlob(blob: Blob, name: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.append(a);
  a.click();
  a.remove();
  // click() has already handed the download to the browser; the URL only
  // has to outlive the moment it starts.
  window.setTimeout(() => URL.revokeObjectURL(url), 30_000);
}

export default function TaskDetail() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const catalog = useCatalog();
  const [task, setTask] = useState<TaskView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [artifacts, setArtifacts] = useState<Record<string, ArtifactView>>({});
  const [previews, setPreviews] = useState<Record<string, string>>({});
  const [excluded, setExcluded] = useState<Set<string>>(new Set());
  const [view, setView] = useState<"flat" | "grouped">("flat");
  const [openId, setOpenId] = useState<string | null>(null);
  const closeDrawer = useCallback(() => setOpenId(null), []);
  // The sheet's tiles glide to their new places when the panel opens or closes.
  const pageRef = useRef<HTMLDivElement>(null);
  useFlip(pageRef, !!openId);
  const [busy, setBusy] = useState<{ label: string; done: number; total: number } | null>(null);
  const [batchError, setBatchError] = useState<string | null>(null);
  /** The artifact whose share link was just copied, for a brief "Copied"
   * on its button; cleared on a timer. */
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const objectUrls = useRef<string[]>([]);

  const load = useCallback(async () => {
    try {
      const t = await getTask(id);
      setTask(t);
    } catch (e) {
      setError(e instanceof Error ? e.message : "failed to load task");
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  // Poll while the Task is still active; a fixed short interval is
  // simplest and matches the other services' own dev poll intervals
  // (deploy/dev.env.example) -- no need for backoff on a page a human is
  // actively looking at.
  useEffect(() => {
    if (!task || !ACTIVE_STATES.has(task.state)) return;
    const timer = setInterval(() => void load(), 2000);
    return () => clearInterval(timer);
  }, [task, load]);

  const doneSlots = useMemo(() => (task?.results ?? []).filter((r) => r.state === "DONE" && r.artifact_id), [task]);
  const dims = useMemo(() => slotDims(task?.results ?? []), [task]);
  const groupDim = useMemo(() => defaultGroupDim(task?.results ?? []), [task]);
  const inputIds = useMemo(() => (task ? inputImageIds(task) : []), [task]);
  const slotByArtifact = useMemo(() => {
    const map = new Map<string, ResultSlot>();
    for (const s of doneSlots) if (s.artifact_id) map.set(s.artifact_id, s);
    return map;
  }, [doneSlots]);

  // Every artifact id this page may need: the inputs (group leads) first,
  // then the results in slot order.
  const wantedIds = useMemo(
    () => [...new Set([...inputIds, ...doneSlots.map((s) => s.artifact_id as string)])],
    [inputIds, doneSlots],
  );

  // Fetch (once) the Artifact view for each of those, so previews and
  // downloads have a download_url/download_token to work with.
  useEffect(() => {
    const missing = wantedIds.filter((aid) => !(aid in artifacts));
    if (missing.length === 0) return;
    void Promise.all(
      missing.map(async (aid) => {
        try {
          const v = await getArtifact(aid);
          setArtifacts((prev) => ({ ...prev, [aid]: v }));
        } catch {
          /* leave unset; the tile renders without a preview or a download */
        }
      }),
    );
  }, [wantedIds, artifacts]);

  const previewOf = useCallback(
    async (artifactId: string) => {
      const view_ = artifacts[artifactId];
      if (!view_?.download_url || !view_.download_token) return;
      try {
        const url = await fetchStoreObjectURL(view_.download_url, view_.download_token);
        objectUrls.current.push(url);
        setPreviews((prev) => ({ ...prev, [artifactId]: url }));
      } catch {
        /* a tile without a preview is still a tile */
      }
    },
    [artifacts],
  );

  // Eager previews for the leading slice of the sheet.
  useEffect(() => {
    for (const aid of wantedIds.slice(0, PREVIEW_EAGER)) {
      if (previews[aid]) continue;
      void previewOf(aid);
    }
  }, [wantedIds, previews, previewOf]);

  useEffect(() => {
    return () => {
      for (const url of objectUrls.current) URL.revokeObjectURL(url);
    };
  }, []);

  /** An artifact's bytes, re-minting the read token once if the one we
   * hold is refused: read tokens do not expire by default (ADR-0019), but
   * an operator can give them a TTL (CORE_ARTIFACT_READ_TTL_S), and a
   * gallery left open longer than that would otherwise fail every
   * download with an opaque 401. */
  const bytesFor = useCallback(
    async (artifactId: string): Promise<Uint8Array<ArrayBuffer>> => {
      const known = artifacts[artifactId];
      if (!known?.download_url || !known.download_token) {
        throw new Error("no download available for " + artifactId.slice(0, 8));
      }
      try {
        return await fetchStoreBytes(known.download_url, known.download_token);
      } catch (e) {
        if (!(e instanceof StoreError) || (e.status !== 401 && e.status !== 403)) throw e;
        const fresh = await getArtifact(artifactId);
        setArtifacts((prev) => ({ ...prev, [artifactId]: fresh }));
        if (!fresh.download_url || !fresh.download_token) throw e;
        return await fetchStoreBytes(fresh.download_url, fresh.download_token);
      }
    },
    [artifacts],
  );

  const fileNameFor = useCallback(
    (artifactId: string) => {
      const slot = slotByArtifact.get(artifactId);
      const type = artifacts[artifactId]?.content_type;
      if (slot && task) return sanitizeName(resultFileName(task.task_id, slot, type, dims));
      const ext = extensionFor(type);
      return sanitizeName(`${task ? `task-${task.task_id.slice(0, 8)}` : "result"}/${artifactId.slice(0, 8)}.${ext}`);
    },
    [artifacts, dims, slotByArtifact, task],
  );

  const selectedIds = useMemo(
    () => doneSlots.map((s) => s.artifact_id as string).filter((aid) => !excluded.has(aid)),
    [doneSlots, excluded],
  );

  const sizes = totalBytes(selectedIds.map((aid) => artifacts[aid]?.size));

  function toggle(artifactId: string) {
    setExcluded((prev) => {
      const next = new Set(prev);
      if (next.has(artifactId)) next.delete(artifactId);
      else next.add(artifactId);
      return next;
    });
  }

  function setAll(keep: boolean) {
    setExcluded(keep ? new Set() : new Set(doneSlots.map((s) => s.artifact_id as string)));
  }

  async function onDownloadOne(artifactId: string) {
    setBatchError(null);
    setBusy({ label: "saving", done: 0, total: 1 });
    try {
      const bytes = await bytesFor(artifactId);
      saveBlob(
        new Blob([bytes], { type: artifacts[artifactId]?.content_type ?? "application/octet-stream" }),
        downloadFileName(fileNameFor(artifactId)),
      );
    } catch (e) {
      setBatchError(e instanceof Error ? e.message : "download failed");
    } finally {
      setBusy(null);
    }
  }

  /** The batch as one file: a zip built in the browser (lib/zip.ts -- Store
   * exposes one artifact at a time and Core has no bundle endpoint, so a
   * server-side zip would be a new contract, not a UI feature). */
  async function onZip() {
    if (!task || selectedIds.length === 0) return;
    setBatchError(null);
    // A non-zip64 archive addresses at most 4 GiB. Core reports sizes for
    // committed artifacts, so an oversized batch is refused before any
    // bytes are fetched rather than after.
    if (sizes.known > ZIP_MAX_TOTAL_BYTES) {
      setBatchError(`this batch is ${formatBytes(sizes.known)}, more than a single zip can hold. Download it one file at a time.`);
      return;
    }
    setBusy({ label: "packing", done: 0, total: selectedIds.length });
    try {
      const entries: ZipEntry[] = [];
      for (const [i, aid] of selectedIds.entries()) {
        const data = await bytesFor(aid);
        entries.push({ name: fileNameFor(aid), data });
        setBusy({ label: "packing", done: i + 1, total: selectedIds.length });
      }
      const names = uniqueNames(entries.map((e) => e.name));
      const zip = buildZip(entries.map((e, i) => ({ name: names[i], data: e.data })));
      saveBlob(zip, `gpu-fan-${task.task_id.slice(0, 8)}.zip`);
    } catch (e) {
      setBatchError(e instanceof Error ? e.message : "could not build the zip");
    } finally {
      setBusy(null);
    }
  }

  /** The same batch as separate files. Browsers save these one after
   * another and usually ask to allow repeated downloads -- slower and
   * noisier than the zip, but it needs no archive and no extra memory. */
  async function onEach() {
    if (!task || selectedIds.length === 0) return;
    setBatchError(null);
    setBusy({ label: "saving", done: 0, total: selectedIds.length });
    try {
      for (const [i, aid] of selectedIds.entries()) {
        const data = await bytesFor(aid);
        saveBlob(
          new Blob([data], { type: artifacts[aid]?.content_type ?? "application/octet-stream" }),
          downloadFileName(fileNameFor(aid)),
        );
        setBusy({ label: "saving", done: i + 1, total: selectedIds.length });
      }
    } catch (e) {
      setBatchError(e instanceof Error ? e.message : "download failed");
    } finally {
      setBusy(null);
    }
  }

  async function onCancel() {
    await cancelTask(id);
    await load();
  }

  /** Put the result's share link on the clipboard (ADR-0019: the download
   * URL with the read token in it, good until the artifact is deleted).
   * The token we hold is used as-is: it does not expire by default, and if
   * an operator has set a TTL the link expires with it, which is what
   * they asked for. */
  async function onCopyLink(artifactId: string) {
    setBatchError(null);
    const link = shareLink(artifacts[artifactId]);
    if (!link) {
      setBatchError("no link yet for " + artifactId.slice(0, 8) + ": the result is still being committed.");
      return;
    }
    try {
      await navigator.clipboard.writeText(link);
      setCopiedId(artifactId);
      window.setTimeout(() => setCopiedId((cur) => (cur === artifactId ? null : cur)), 1500);
    } catch {
      // Clipboard access needs a secure context and a user gesture; when
      // it is refused, show the link so it can still be copied by hand.
      setBatchError("could not copy to the clipboard. The link is: " + link);
    }
  }

  function useAsInput(slot: ResultSlot) {
    if (!slot.artifact_id || !task) return;
    const view_ = artifacts[slot.artifact_id];
    navigate("/", {
      state: {
        artifactId: slot.artifact_id,
        artifactLabel: `run ${task.task_id.slice(0, 8)} / ${slotLabel(slot)}`,
        downloadUrl: view_?.download_url,
        downloadToken: view_?.download_token,
      },
    });
  }

  if (error) {
    return (
      <div className="u-page">
        <p className="u-error">{error}</p>
      </div>
    );
  }
  if (!task) {
    return (
      <div className="u-page">
        <p className="u-loading">Loading…</p>
      </div>
    );
  }

  const active = ACTIVE_STATES.has(task.state);
  // ADR-0047: the Catalog's cancel flag hides the button (a cancelled DFR run
  // could poison its worker, 12-providers.md 4.19); hidden until it loads.
  const canCancel = catalog?.features.cancel === true && !TERMINAL_TASK_STATES.has(task.state);
  const percent = task.requested > 0 ? Math.round((task.done / task.requested) * 100) : 0;
  const openSlot = openId ? slotByArtifact.get(openId) : undefined;
  const openView = openId ? artifacts[openId] : undefined;

  // `index` is the tile's position in the list it is rendered into, which
  // is the only thing unique about it: a slot coordinate is not unique when
  // a job has more than one output (echo_v2_dev declares `image` and `prompt`,
  // so two result rows share {image, seed} and neither the label nor the
  // file name can tell them apart). The list order is fixed by Core and
  // only ever appended to, so a position is a stable key.
  function tile(slot: ResultSlot, index: number) {
    const aid = slot.artifact_id as string | undefined;
    const label = slotLabel(slot, groupDim && view === "grouped" ? dims.filter((d) => d !== groupDim) : undefined);
    const isDone = slot.state === "DONE" && !!aid;
    const inBatch = isDone && !excluded.has(aid as string);
    const preview = aid ? previews[aid] : undefined;
    const video = isVideo(artifacts[aid ?? ""]?.content_type);
    // Optional-chained: a row can be DONE before its Artifact view has
    // arrived, and a Record<> index is typed non-optional regardless.
    const size = aid ? artifacts[aid]?.size : undefined;
    const printing = slot.state === "RUNNING" || (active && slot.state === "PENDING");
    return (
      <li
        key={index}
        data-flip={String(index)}
        className={
          "u-frame" + (inBatch ? " u-frame--on" : "") + (slot.state === "FAILED" ? " u-frame--failed" : "")
        }
      >
        {inBatch && <span className="u-frame__tick" />}
        {isDone ? (
          <button className="u-frame__stage" onClick={() => setOpenId(aid as string)} title="Inspect this result">
            {preview ? (
              video ? (
                <video src={preview} muted playsInline preload="metadata" />
              ) : (
                <img src={preview} alt={label} />
              )
            ) : (
              <span className="u-frame__pending">load preview</span>
            )}
          </button>
        ) : (
          <div className="u-frame__stage u-frame__stage--empty">
            {printing && <span className="u-frame__scan" />}
            <span className="u-frame__pending">{slot.state.toLowerCase()}</span>
          </div>
        )}
        <div className="u-frame__cap">
          <span className="u-frame__name">{label}</span>
          <span className="u-frame__meta">
            {slot.state !== "DONE"
              ? slot.state.toLowerCase()
              : !aid
                ? // A slot can finish without producing a file: a job with
                  // more than one output (echo_v2_dev's `prompt` next to its
                  // `image`) fills a result row with a value that is not an
                  // artifact. Saying so beats an empty tile that reads like
                  // a missing download.
                  "no file"
                : size
                  ? formatBytes(size)
                  : "done"}
          </span>
        </div>
        {isDone && (
          <div className="u-frame__acts">
            <button className="u-btn u-btn--quiet" aria-pressed={inBatch} onClick={() => toggle(aid as string)}>
              {inBatch ? "In batch" : "Out"}
            </button>
            <button className="u-btn u-btn--quiet" onClick={() => void onDownloadOne(aid as string)}>
              Download
            </button>
            <button
              className="u-btn u-btn--quiet"
              onClick={() => void onCopyLink(aid as string)}
              title="Copy a link to this file. Anyone with the link can open it until the result is deleted."
            >
              {copiedId === aid ? "Copied" : "Copy link"}
            </button>
          </div>
        )}
      </li>
    );
  }

  return (
    <div className={panelPageClass("u-page", !!openId)} ref={pageRef}>
      <div className="u-head">
        <div>
          <h1 className="u-h1">Run {task.task_id.slice(0, 8)}</h1>
          <p className="u-lede">
            {task.alias} · started {formatWhen(task.created_at)}
            {task.finished_at ? ` · finished ${formatWhen(task.finished_at)}` : ""}
          </p>
        </div>
        {doneSlots.length > 0 && (
          <div className="u-viewswitch">
            <button aria-pressed={view === "flat"} onClick={() => setView("flat")} title="Every result once, in slot order">
              1:1
            </button>
            <button
              aria-pressed={view === "grouped"}
              onClick={() => setView("grouped")}
              title={groupDim ? `Grouped by ${groupDim}` : "Grouped by input"}
            >
              1:N
            </button>
          </div>
        )}
      </div>

      <div className="u-summary">
        <span className={`u-state u-state--${task.state.toLowerCase()}`}>{task.state.toLowerCase()}</span>
        <span>
          {task.done} / {task.requested} results
        </span>
        {task.failed > 0 && <span className="u-state u-state--failed">{task.failed} failed</span>}
        {task.cancelled > 0 && <span className="u-state u-state--cancelled">{task.cancelled} cancelled</span>}
        <span>
          {formatMinor(task.quoted_total, task.currency)}
          {task.refunded > 0 ? ` · refunded ${formatMinor(task.refunded, task.currency)}` : ""}
        </span>
        <span className="u-summary__spacer" />
        {canCancel && (
          <button className="u-btn u-btn--quiet" onClick={() => void onCancel()}>
            Cancel run
          </button>
        )}
      </div>

      {active && (
        <div className="u-print">
          <div className="u-bar">
            <span className="u-bar__fill" style={{ width: `${percent}%` }} />
            <span className="u-bar__stripes" />
          </div>
          <p className="u-note">
            {task.state === "QUEUED"
              ? "queued — waiting for a GPU. Results appear here as they land."
              : `printing ${task.done} of ${task.requested}. Results appear here as they land.`}
          </p>
        </div>
      )}

      {task.results.length === 0 ? (
        <p className="u-empty">This run has no results yet.</p>
      ) : view === "grouped" && groupDim ? (
        groupSlots(task.results, groupDim).map((group) => (
          <section className="u-group" key={group.index}>
            <div className="u-group__head">
              {previews[inputIds[group.index]] && (
                <img className="u-group__lead" src={previews[inputIds[group.index]]} alt="" />
              )}
              <span className="u-group__title">
                {groupDim} {group.index}
              </span>
              <span className="u-note">
                {group.slots.length} result{group.slots.length === 1 ? "" : "s"}
              </span>
            </div>
            <ul className="u-grid">{group.slots.map((s, i) => tile(s, i))}</ul>
          </section>
        ))
      ) : (
        <ul className="u-grid u-grid--wide" data-testid="result-grid">
          {task.results.map((s, i) => tile(s, i))}
        </ul>
      )}

      {doneSlots.length > 0 && (
        <div className="u-batch">
          <span className="u-batch__count">
            {selectedIds.length} of {doneSlots.length} in the batch
          </span>
          <span className="u-batch__meta">
            {sizes.known > 0 && `at least ${formatBytes(sizes.known)}`}
            {sizes.unknown > 0 ? ` · ${sizes.unknown} size${sizes.unknown === 1 ? "" : "s"} unknown` : ""}
          </span>
          {busy && (
            <>
              <span className="u-batch__progress">
                <span style={{ width: `${busy.total ? Math.round((busy.done / busy.total) * 100) : 0}%` }} />
              </span>
              <span className="u-batch__meta">
                {busy.label} {busy.done} of {busy.total}
              </span>
            </>
          )}
          <span className="u-batch__acts">
            <button className="u-btn u-btn--quiet" onClick={() => setAll(selectedIds.length === 0)}>
              {selectedIds.length === 0 ? "Select all" : "Select none"}
            </button>
            <button className="u-btn" disabled={!!busy || selectedIds.length === 0} onClick={() => void onEach()}>
              Separate files
            </button>
            <button
              className="u-btn u-btn--red"
              disabled={!!busy || selectedIds.length === 0}
              onClick={() => void onZip()}
            >
              Download ZIP
            </button>
          </span>
        </div>
      )}

      {batchError && <p className="u-error">{batchError}</p>}

      <Drawer
        open={!!openId}
        title={openSlot ? slotLabel(openSlot) : "result"}
        label={openSlot ? slotLabel(openSlot) : "result"}
        onClose={closeDrawer}
      >
        {openId && (
          <>
          <div className="u-drawer__media">
            {previews[openId] ? (
              isVideo(openView?.content_type) ? (
                <video src={previews[openId]} controls autoPlay loop muted playsInline />
              ) : (
                <img src={previews[openId]} alt="" />
              )
            ) : (
              <p className="u-note" style={{ padding: 18 }}>
                Loading…
              </p>
            )}
          </div>
          <dl className="u-drawer__meta">
            <dt>File</dt>
            <dd>{fileNameFor(openId)}</dd>
            <dt>Type</dt>
            <dd>{openView?.content_type ?? "—"}</dd>
            <dt>Size</dt>
            <dd>{openView?.size ? formatBytes(openView.size) : "—"}</dd>
            <dt>State</dt>
            <dd>{openSlot?.state.toLowerCase() ?? "—"}</dd>
            {openSlot?.prompt && (
              <>
                <dt>Prompt</dt>
                <dd data-testid="slot-prompt">{openSlot.prompt}</dd>
              </>
            )}
            <dt>Artifact</dt>
            <dd>{openId}</dd>
          </dl>
          <div className="u-drawer__acts">
            <button className="u-btn" onClick={() => void onDownloadOne(openId)}>
              Download
            </button>
            <button
              className="u-btn"
              onClick={() => void onCopyLink(openId)}
              title="Copy a link to this file. Anyone with the link can open it until the result is deleted."
            >
              {copiedId === openId ? "Copied" : "Copy link"}
            </button>
            <button className="u-btn" onClick={() => toggle(openId)} aria-pressed={!excluded.has(openId)}>
              {excluded.has(openId) ? "Add to batch" : "Remove from batch"}
            </button>
            {openSlot && (
              <button className="u-btn u-btn--quiet" onClick={() => useAsInput(openSlot)}>
                Use as input
              </button>
            )}
          </div>
          </>
        )}
      </Drawer>
    </div>
  );
}
